import { router } from '@inertiajs/react'
import { BarChart3, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import StatsCards from './StatsCards'
import Filters from './Filters'
import { MATURITY_LABELS, REVENUE_LABELS, VERTICAL_LABELS, PATH_LABELS, TEAM_SIZE_LABELS } from './constants'
import type { PanoramaResponseRow, Pagination, Filters as FiltersType, Stats } from './constants'

interface Props {
  responses: PanoramaResponseRow[]
  pagination: Pagination
  search: string
  filters: FiltersType
  stats: Stats
}

function maturityBadgeClasses(level: number) {
  if (level >= 4) return 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400'
  if (level === 3) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-400'
  return 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400'
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

function PanoramaResponsesIndex({ responses, pagination, search, filters, stats }: Props) {
  const goToPage = (page: number) => {
    router.get('/cc/panorama_responses', {
      search: search || undefined,
      maturity_level: filters.maturityLevel || undefined,
      respondent_path: filters.respondentPath || undefined,
      revenue_range: filters.revenueRange || undefined,
      vertical: filters.vertical || undefined,
      page,
    }, { preserveState: true })
  }

  const handleExport = () => {
    const params = new URLSearchParams()
    if (filters.maturityLevel) params.set('maturity_level', filters.maturityLevel)
    if (filters.respondentPath) params.set('respondent_path', filters.respondentPath)
    if (filters.revenueRange) params.set('revenue_range', filters.revenueRange)
    if (filters.vertical) params.set('vertical', filters.vertical)
    if (search) params.set('search', search)
    const qs = params.toString()
    window.location.href = `/cc/panorama_responses/export_csv${qs ? `?${qs}` : ''}`
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-6 w-6 text-foreground" />
          <h2 className="text-2xl font-bold text-foreground">Panorama E-commerce 2026</h2>
          <span className="text-sm text-muted-foreground">({pagination.total})</span>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      <StatsCards stats={stats} />
      <Filters filters={filters} search={search} />

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-220px)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-muted border-b border-border">
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Faturamento</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Equipe</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Vertical</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Caminho</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Maturidade</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Concluído em</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {responses.map((r) => (
                <tr key={r.id} className="hover:bg-accent transition-colors">
                  <td className="px-4 py-3 text-sm text-foreground">{r.email}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{REVENUE_LABELS[r.revenueRange] ?? r.revenueRange}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{TEAM_SIZE_LABELS[r.teamSize] ?? r.teamSize}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{VERTICAL_LABELS[r.vertical] ?? r.vertical}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      r.respondentPath === 'ai_user'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-500/15 dark:text-gray-400'
                    }`}>
                      {PATH_LABELS[r.respondentPath] ?? r.respondentPath}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${maturityBadgeClasses(r.maturityLevel)}`}>
                      {r.maturityLevel} — {MATURITY_LABELS[r.maturityLevel] ?? ''}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(r.completedAt)}</td>
                </tr>
              ))}
              {responses.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Nenhuma resposta encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
            <span className="text-sm text-muted-foreground">
              Página {pagination.page} de {pagination.totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => goToPage(pagination.page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => goToPage(pagination.page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

PanoramaResponsesIndex.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default PanoramaResponsesIndex
